import { OfficesService } from '@/services/api/franchises.service';
import { PermsService } from '@/services/api/perms.service';
import { CommonModule } from '@angular/common';
import { Component, effect, EventEmitter, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Franchise } from 'src/app/models/franchise.model';

@Component({
  selector: 'app-perm-editor',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SelectModule, ButtonModule],
  template: `
  <div class="grid formgrid p-fluid">
    <div class="field mb-4 col-12 md:col-6">
        <label for="title" class="font-medium text-900">Title</label>
        <input id="title" type="text" [(ngModel)]="title" pInputText>
    </div>
    <div class="field mb-4 col-12 md:col-6">
        <label for="description" class="font-medium text-900">Description</label>
        <input id="description" type="text" [(ngModel)]="permDesc" pInputText>
    </div>
    <div class="field mb-4 col-12 md:col-4">
        <label for="bio" class="font-medium text-900">Office</label>
        <p-select [options]="offices" filter [(ngModel)]="selectedOffice" [filter]="true"
        filterBy="Title" optionLabel="Title" optionValue="FID"
            [style]="{'width':'100%'}" placeholder="Select an office" />
    </div>
    <div class="field mb-4 col-12 md:col-4">
        <label for="email" class="font-medium text-900">Table</label>
        <p-select [options]="tableOptions" [filter]="true"
        filterBy="label" [(ngModel)]="selectedTable" optionValue="value" [style]="{'width':'100%'}"
            placeholder="Select a table" />
    </div>
    <div class="field mb-4 col-12 md:col-4">
        <label for="country" class="font-medium text-900">Action</label>
        <p-select [options]="typeOptions" [filter]="true"
        filterBy="label" [(ngModel)]="selectedType" optionValue="value" [style]="{'width':'100%'}"
            placeholder="Select a table" />
    </div>
    <div class="col-12">
        <button pButton pRipple label="Save Changes" class="w-auto mt-3" [disabled]="checkPermission()" (click)="onSave()"></button>
    </div>
</div>
  `,
  styles:``
})
export class PermEditorComponent {
  typeOptions = [
    { label: 'Admin', value: 'admin' },
    { label: 'Approve', value: 'approve' },
    { label: 'Edit', value: 'edit' },
    { label: 'Notify', value: 'notify' },
    { label: 'Price', value: 'price' },
    { label: 'Sign', value: 'sign' },
    { label: 'View', value: 'view' },
  ];

  officeOptions: Franchise[] = [];

  selectedType: string = '';
  selectedTable: string = '';
  selectedOffice: number | null = null;
  permDesc: string = '';
  title: string = '';

  tableOptions = [
    { label: 'approvalResponses', value: 'approvalResponses' },
    { label: 'BinderCases', value: 'BinderCases' },
    { label: 'binderFiles', value: 'binderFiles' },
    { label: 'Binders', value: 'Binders' },
    { label: 'EngineeringContacts', value: 'EngineeringContacts' },
    { label: 'exemptions', value: 'exemptions' },
    { label: 'lenderContacts', value: 'lenderContacts' },
    { label: 'Lenders', value: 'Lenders' },
    { label: 'ModLevels', value: 'ModLevels' },
    { label: 'PermRoles', value: 'PermRoles' },
    { label: 'PlanPricing', value: 'PlanPricing' },
    { label: 'promotions', value: 'promotions' },
    { label: 'UserRoles', value: 'UserRoles' },
    { label: 'Users', value: 'Users' }
  ];

  @Output() permSaved = new EventEmitter<void>();

  constructor(private permissionsService: PermsService, public officeService: OfficesService) {
    effect(() => {
      const perm = this.permissionsService.selectedPerm();
      if(perm) {
        if(perm.FID === 0 || perm.FID === null) {
          this.selectedOffice = 5;
        } else {
          this.selectedOffice = perm.FID;
        }
        this.selectedTable = perm.dataTable;
        this.selectedType = perm.permType;
        this.title = perm.PermTitle;
        this.permDesc = perm.permDesc;
      } else {
        this.title = '';
        this.selectedOffice = null;
        this.selectedTable = '';
        this.selectedType = '';
        this.permDesc = '';
      }
    });
  }

  get offices() {
    return this.officeService.offices().filter(office => office.Active === true || office.FID === 5);
  }

  onSelectOffice(event: any) {
    console.log('selected office:', event);
    this.selectedOffice = event.value;
  }

  findOffice(fid: number) {
    if(fid === 0) {
      return this.officeService.offices().find(office => office.FID === 5) ?? null;
    } else {
      return this.officeService.offices().find(office => office.FID === fid) ?? null;
    }
  }

  onSelectType(event: any) {
    this.selectedType = event.value;
  }

  onSelectTable(event: any) {
    this.selectedTable = event.value;
  }

  onSave() {
    let fid = this.selectedOffice
    if(fid === 5 || fid === null) {
      fid = 0;
    }
    console.log('saving');
    const permdata = {
      FID: fid,
      dataTable: this.selectedTable,
      permType: this.selectedType,
      PermTitle: this.title,
      permDesc: this.permDesc
    }
    this.permissionsService.savePerm(permdata, this.permissionsService.selectedPerm()?.AccessPermID).subscribe(res => {
      this.permissionsService.addOrUpdatePerm(res);
      this.permSaved.emit();
    });
  }

  checkPermission(): boolean {
    // Return true if any field is null (to disable the button)
    if (this.selectedOffice === null || this.selectedTable === '' || this.selectedType === '') {
      return true;
    }
  
    const perm = this.permissionsService.permissions().filter(perm => {
      // Handle FID equivalence (5, 0, and null are the same)
      const fidMatch = (this.selectedOffice === 5 || this.selectedOffice === 0) ? 
        (perm.FID === 5 || perm.FID === 0 || perm.FID === null) :
        perm.FID === this.selectedOffice;
  
      return fidMatch && 
        perm.dataTable === this.selectedTable && 
        perm.permType === this.selectedType;
    });
  
    // Return true if any matching permission exists
    return perm.length > 0;
  }
}
